import React, { useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';

interface EnergyRatioChartProps {
  selectedDischargeRatio?: number; // r_Q value to highlight (default 0.7)
}

const EnergyRatioChart: React.FC<EnergyRatioChartProps> = ({
  selectedDischargeRatio = 0.7
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // High-DPI canvas scaling for crisp text
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = 700;
    const displayHeight = 500;

    // Set actual canvas size (accounting for device pixel ratio)
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#1a1a2e' : '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    const padding = 80;
    const chartWidth = displayWidth - padding * 2;
    const chartHeight = displayHeight - padding * 2;

    // Calculate energy ratio based on discharge ratio
    // Formula from IECON2023 paper: V_SC,used/W_total = 1 - r_Q^2
    const calculateEnergyRatio = (r_Q: number): number => {
      return 1 - Math.pow(r_Q, 2);
    };

    // Generate curve data points
    const dataPoints: [number, number][] = [];
    for (let r_Q = 0; r_Q <= 1; r_Q += 0.01) {
      const energyRatio = calculateEnergyRatio(r_Q);
      dataPoints.push([r_Q, energyRatio]);
    }

    // Scaling functions
    const xScale = (r_Q: number) => padding + (r_Q / 1.0) * chartWidth;
    const yScale = (energyRatio: number) => padding + chartHeight - (energyRatio / 1.0) * chartHeight;

    // Draw grid
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#333' : '#e8e8e8';
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = padding + (i / 10) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(padding + chartWidth, y);
      ctx.stroke();

      // Y-axis labels
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
      ctx.font = '11px Arial';
      ctx.textAlign = 'right';
      const value = (1.0 - i / 10).toFixed(1);
      ctx.fillText(value, padding - 10, y + 4);
    }

    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = padding + (i / 10) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, padding + chartHeight);
      ctx.stroke();

      // X-axis labels
      ctx.font = '11px Arial';
      ctx.textAlign = 'center';
      const value = (i / 10).toFixed(1);
      ctx.fillText(value, x, padding + chartHeight + 20);
    }

    // Draw axes
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + chartHeight);
    ctx.stroke();

    // Draw the energy ratio curve
    ctx.strokeStyle = '#1f77b4';
    ctx.lineWidth = 3;
    ctx.beginPath();

    dataPoints.forEach(([r_Q, energyRatio], i) => {
      const x = xScale(r_Q);
      const y = yScale(energyRatio);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Highlight selected discharge ratio
    if (selectedDischargeRatio >= 0 && selectedDischargeRatio <= 1) {
      const energyRatio = calculateEnergyRatio(selectedDischargeRatio);
      const highlightX = xScale(selectedDischargeRatio);
      const highlightY = yScale(energyRatio);

      // Vertical line from point to x-axis
      ctx.strokeStyle = '#ff7f0e';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(highlightX, highlightY);
      ctx.lineTo(highlightX, padding + chartHeight);
      ctx.stroke();

      // Horizontal line from point to y-axis
      ctx.beginPath();
      ctx.moveTo(highlightX, highlightY);
      ctx.lineTo(padding, highlightY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw point
      ctx.fillStyle = '#ff7f0e';
      ctx.beginPath();
      ctx.arc(highlightX, highlightY, 6, 0, 2 * Math.PI);
      ctx.fill();

      // Label for selected point
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(selectedDischargeRatio.toFixed(1), highlightX, padding + chartHeight + 35);

      ctx.textAlign = 'right';
      ctx.fillText(energyRatio.toFixed(2), padding - 25, highlightY + 4);
    }

    // Axis labels
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';

    // X-axis label
    ctx.fillText('Discharge Ratio r_Q', displayWidth / 2, displayHeight - 20);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(20, displayHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Energy Ratio V_SC,used/W_total', 0, 0);
    ctx.restore();

    // Title
    ctx.font = 'bold 16px Arial';
    ctx.fillText('Energy Ratio versus Discharge Ratio', displayWidth / 2, 30);

    // Add key points annotations
    ctx.font = '11px Arial';
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#aaa' : '#666';

    // Point at r_Q = 0.5 - verschoben weiter nach rechts
    const r_05_x = xScale(0.5);
    const r_05_y = yScale(calculateEnergyRatio(0.5));
    ctx.textAlign = 'left';
    ctx.fillText('(0.5, 0.75)', r_05_x + 15, r_05_y - 10);

    // Point at r_Q = 1.0 - verschoben nach unten rechts
    ctx.textAlign = 'right';
    ctx.fillText('(1.0, 0.0)', xScale(1.0) - 5, yScale(0) + 35);

  }, [selectedDischargeRatio, theme]);

  return (
    <Box sx={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '8px'
        }}
      />
    </Box>
  );
};

export default EnergyRatioChart;
